import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';

export interface Reservation {
  id: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  product_type: string;
  product_id: string | null;
  product_name: string;
  date_debut: string;
  date_fin: string;
  status: string;
  message: string | null;
  created_at: string;
  updated_at: string;
}

export const useReservations = () => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchReservations = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('reservations' as any)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReservations((data as any[]) || []);
    } catch (error) {
      logger.error('Erreur chargement réservations', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateStatus = async (id: string, status: string) => {
    try {
      const { error } = await supabase
        .from('reservations' as any)
        .update({ status } as any)
        .eq('id', id);

      if (error) throw error;
      
      setReservations(prev => 
        prev.map(r => r.id === id ? { ...r, status } : r)
      );
      
      toast({
        title: "Statut mis à jour",
        description: `La réservation est maintenant "${status === 'loue' ? 'Loué' : status === 'en_attente' ? 'En attente' : 'Annulé'}"`,
      });
    } catch (error) {
      logger.error('Erreur mise à jour statut', error);
      toast({ title: "Erreur", description: "Impossible de mettre à jour le statut", variant: "destructive" });
    }
  };

  const deleteReservation = async (id: string) => {
    try {
      const { error } = await supabase
        .from('reservations' as any)
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setReservations(prev => prev.filter(r => r.id !== id));
      toast({ title: "Supprimée", description: "La réservation a été supprimée" });
    } catch (error) {
      logger.error('Erreur suppression réservation', error);
      toast({ title: "Erreur", description: "Impossible de supprimer", variant: "destructive" });
    }
  };

  const createReservation = async (data: Omit<Reservation, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { error } = await supabase
        .from('reservations' as any)
        .insert(data as any);

      if (error) throw error;
      return true;
    } catch (error) {
      logger.error('Erreur création réservation', error);
      return false;
    }
  };

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  return {
    reservations,
    loading,
    fetchReservations,
    updateStatus,
    deleteReservation,
    createReservation,
  };
};
